/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Language} from "types/entities/Language";
import {HallCode} from "types/enums/HallCode";
import {TrackDomain} from "types/enums/TrackDomain";

export const GOOGLE_MAPS_PUBLIC_KEY = "AIzaSyBIY3soC-h5uu1JGG89cLGXgVQWXK108Ws";
// export const GOOGLE_ANALYTICS_KEY = "UA-60205697-4";

export const SELLER_API_KEY = "777f80c6-6d00-11ee-843d-88d7f67de6c2";

export const PROTOCOL = "https";
export const IS_DEV = ['localhost', '127.0.0.1'].includes(document.location.hostname) || document.location.port?.includes("5173") || false;
export const IS_REMOTE_DEV = document.location.hostname.includes("192.168") || document.location.hostname.includes("staging.arbopark.ch");
export const IS_PROD = !IS_DEV && !IS_REMOTE_DEV;

/** PostHog project API key (Project Settings → Project API Key), e.g. `phc_...` */
export const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || "";
/** PostHog ingest host — EU cloud default; use `https://us.i.posthog.com` for US */
export const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://eu.i.posthog.com";
/** Runs on production only (e.g. seller.arbopark.ch) — off on localhost, Vite, staging, LAN */
export const POSTHOG_ENABLED = IS_PROD && Boolean(POSTHOG_KEY);
export const TRACK_DOMAIN = TrackDomain.ArboPark;
export const ROOT_STORAGE_DOMAIN = IS_DEV ? "cl.arbopark.ch" : "cl.arbopark.ch";
export const ROOT_STORAGE_WEB = `${PROTOCOL}://${ROOT_STORAGE_DOMAIN}/`;
export const ROOT_API_WEB = `${PROTOCOL}://${ROOT_STORAGE_DOMAIN}`;
export const ROOT_API = ROOT_API_WEB + "/api";
export const DEFAULT_HALL: HallCode = HallCode.ArboPark;
export const ACTIVITIES_SELECTION_ALLOWED: boolean = false;
export const GROUP_TYPES_ALLOWED: boolean = false;
export const SELECT_CUSTOM_TIMESLOTS_ALLOWED: boolean = false;
export const USE_POSSIBLE_TIMESLOTS_ALLOWED: boolean = false;
export const DRIVERS_NAMES_CHECKIN_ALLOWED: boolean = false;
export const SHOW_ITEM_SLOT_RANGE_ONLY: boolean = true;
export const SHOW_ONLY_RESERVATION_START: boolean = true;
export const FILTER_TIMESLOTS_PER_PERSON_TYPE: boolean = false;
export const SHOW_TYPICAL_TIMEPLAN: boolean = false;
export const SHOW_STANDARD_PRICE: boolean = false;
export const SHOW_BOOKABLE_DAYS_FROM_NOW: boolean = false;
export const ENABLE_HELP_WITH_TIMESLOTS: boolean = false;
export const ENABLED_COUNTDOWN_TIMER: boolean = false;
export const SHOW_NOTIFICATION_METHODS: boolean = false;
export const IS_VOUCHER_ALLOWED_IN_SHOPPING_CART: boolean = true;
export const FELIXTAG_VOUCHER_BANNER_ENABLED: boolean = false;

export const VOUCHERS_LIMIT: number = 10; //limit to max 100 vouchers

export const MIN_PERSON_BIRTHDATE_AGE = 8;

export const MIN_COUNT_FOR_INQUIRY = 100;

export const SELL_GIFTCARD_VOUCHERS = true;
export const SHOW_TIMESLOT_OCCUPANCY = false;
export const SHOW_IMPORTANT_ARRIVE_TIME = false;

export const DEFAULT_COUNTRY_CODE = "CH";
export const REQUIRE_FULL_ADDRESS_IN_REGISTRATION = true;
export const GUEST_PHONE_EMAIL_MANDATORY = true;
export const GUEST_ADDRESS_MANDATORY = true;

export const EMAIL_LOGIN_REQUIRED = true;
export const EMAIL_VERIFICATION_REQUIRED = false;
export const SHOW_PRICE_PER_PERSON_FOR_PACKAGE_CATEGORY_EVENT = true;
export const MAX_MONTH_COUNT_FOR_AVAILABLE_DATES = 7;
export const LOGIN_CODE_LENGTH = 4;

const HALL_CONFIGS: Record<string, any> = {
    [HallCode.Winterthur]: {
        currency: "CHF",
        fractionDigits: 2,
        locale: "de-CH",
        tax: "8.1",
        defaultLanguage: "de",
        hallId: 1,
        daysLimit: 3,
        daysFromNow: 0,
        officialName: "Tempodrom Winterthur",
        arrivalMinutes: 20,
        googleAnalytics: "UA-90898326-2",
        googleTagManager: "GTM-K499QS6",
        basename: "winterthur",
        languages: [
            { id: 1, code: "en", locale: "en-GB", name: "English" },
            { id: 2, code: "de", locale: "de-DE", name: "Deutsch" },
        ],
        isOnlyBooking: true,
        hasBasename: true,
    },
    [HallCode.Spreitenbach]: {
        currency: "CHF",
        fractionDigits: 2,
        locale: "de-CH",
        tax: "8.1",
        defaultLanguage: "de",
        hallId: 2,
        daysLimit: 3,
        daysFromNow: 0,
        officialName: "Kartbahn Spreitenbach",
        arrivalMinutes: 15,
        googleAnalytics: "UA-90898326-3",
        googleTagManager: "GTM-K499QS6",
        basename: "spreitenbach-test",
        languages: [
            { id: 1, code: "en", locale: "en-GB", name: "English" },
            { id: 2, code: "de", locale: "de-DE", name: "Deutsch" },
        ],
        isOnlyBooking: true,
        hasBasename: true,
    },
    [HallCode.Budapest]: {
        currency: "HUF",
        fractionDigits: 0,
        locale: "hu-HU",
        tax: "27",
        defaultLanguage: "hu",
        hallId: 4,
        daysLimit: 5,
        daysFromNow: 3,
        officialName: "FlashKart Budapest",
        arrivalMinutes: 15,
        googleAnalytics: "UA-2773318-72",
        googleTagManager: "GTM-NQJNVGG",
        basename: "blaha",
        languages: [
            { id: 1, code: "hu", locale: "hu-HU", name: "Magyar" },
            { id: 2, code: "en", locale: "en-GB", name: "English" },
        ],
        isOnlyBooking: false,
        hasBasename: false,
    },
    [HallCode.BudapestKoki]: {
        currency: "HUF",
        fractionDigits: 0,
        locale: "hu-HU",
        tax: "27",
        defaultLanguage: "hu",
        hallId: 7,
        daysLimit: 5,
        daysFromNow: 3,
        officialName: "FlashKart Budapest",
        arrivalMinutes: 15,
        googleAnalytics: "UA-2773318-72",
        googleTagManager: "GTM-NQJNVGG",
        basename: "koki",
        languages: [
            { id: 1, code: "en", locale: "en-GB", name: "English" },
            { id: 2, code: "hu", locale: "hu-HU", name: "Magyar" },
        ],
        isOnlyBooking: true,
        hasBasename: true,
    },
    [HallCode.Trencin]: {
        currency: "EUR",
        fractionDigits: 2,
        locale: "sk-SK",
        tax: "20",
        defaultLanguage: "sk",
        hallId: 3,
        daysLimit: 5,
        daysFromNow: 5,
        officialName: "Volt Racing Trenčín",
        arrivalMinutes: 15,
        googleAnalytics: "UA-2773318-72",
        googleTagManager: "GTM-NQJNVGG",
        basename: "trencin",
        languages: [
            { id: 1, code: "en", locale: "en-GB", name: "English" },
            { id: 2, code: "sk", locale: "sk-SK", name: "Slovensky" },
        ],
        isOnlyBooking: true,
        hasBasename: true,
    },
    [HallCode.Zwolle]: {
        currency: "EUR",
        fractionDigits: 2,
        locale: "nl-NL",
        tax: "9",
        defaultLanguage: "nl",
        hallId: 5,
        daysLimit: 7,
        daysFromNow: 0,
        officialName: "Kobalt Zwolle",
        arrivalMinutes: 15,
        googleAnalytics: "UA-2773318-72",
        googleTagManager: "GTM-NQJNVGG",
        basename: "kobalt",
        languages: [
            { id: 1, code: "en", locale: "en-GB", name: "English" },
            { id: 2, code: "de", locale: "de-DE", name: "Deutsch" },
            { id: 3, code: "nl", locale: "nl-NL", name: "Netherlands" },
        ],
        showPerPersonPrice: true,
        showReadTermsButton: true,
        isOnlyBooking: false,
        hasBasename: false,
    },
    [HallCode.Phpcode]: {
        currency: "EUR",
        fractionDigits: 2,
        locale: "nl-NL",
        tax: "9",
        defaultLanguage: "nl",
        hallId: 420,
        daysLimit: 7,
        daysFromNow: 0,
        officialName: "PHPCode",
        arrivalMinutes: 15,
        googleAnalytics: "UA-2773318-72",
        googleTagManager: "GTM-NQJNVGG",
        basename: "phpcode",
        languages: [
            { id: 1, code: "en", locale: "en-GB", name: "English" },
            { id: 2, code: "de", locale: "de-DE", name: "Deutsch" },
        ],
        showPerPersonPrice: true,
        isOnlyBooking: true,
        hasBasename: true,
    },
    [HallCode.Dev]: {
        currency: "EUR",
        fractionDigits: 2,
        locale: "nl-NL",
        tax: "9",
        defaultLanguage: "nl",
        hallId: 420,
        daysLimit: 7,
        daysFromNow: 0,
        officialName: "Dev TOMKART",
        arrivalMinutes: 15,
        googleAnalytics: "UA-2773318-72",
        googleTagManager: "GTM-NQJNVGG",
        basename: "devtomkart",
        showPerPersonPrice: true,
        isOnlyBooking: true,
        hasBasename: true,
    },
    [HallCode.Corner]: {
        currency: "EUR",
        fractionDigits: 2,
        locale: "nl-NL",
        tax: "9",
        defaultLanguage: "nl",
        hallId: 422,
        daysLimit: 7,
        daysFromNow: 0,
        officialName: "Corner",
        arrivalMinutes: 15,
        googleAnalytics: "UA-2773318-72",
        googleTagManager: "GTM-NQJNVGG",
        basename: "corner",
        languages: [
            { id: 1, code: "en", locale: "en-GB", name: "English" },
            { id: 2, code: "de", locale: "de-DE", name: "Deutsch" },
        ],
        showPerPersonPrice: true,
        isOnlyBooking: true,
        hasBasename: true,
    },
    [HallCode.ArboPark]: {
        currency: "CHF",
        fractionDigits: 2,
        locale: "de-CH",
        tax: 8.1,
        defaultLanguage: "de",
        hallId: 1,
        daysLimit: 0,
        daysFromNow: 0,
        officialName: "ArboPark",
        arrivalMinutes: 10,
        googleAnalytics: "G-6L25F5KP4H",
        googleTagManager: "GTM-M36GCVWB",
        basename: "arbopark",
        languages: [
            { id: 1, code: "en", locale: "en-GB", name: "English" },
            { id: 2, code: "de", locale: "de-DE", name: "Deutsch" },
        ],
        isOnlyBooking: true,
        hasBasename: false,
    },
};

const currentConfig = HALL_CONFIGS[DEFAULT_HALL] || HALL_CONFIGS[HallCode.ArboPark];

const HALL_CURRENCY = currentConfig.currency;
const HALL_CURRENCY_FRACTION_DIGITS = currentConfig.fractionDigits;
const HALL_LOCALE = currentConfig.locale;
const HALL_TAX = currentConfig.tax;
const HALL_DEFAULT_LANGUAGE = currentConfig.defaultLanguage;
const HALL_DEFAULT_HALL_ID = currentConfig.hallId;
const HALL_DEFAULT_RESERVATION_DAYS_LIMIT = currentConfig.daysLimit;
const HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = currentConfig.daysFromNow;
const HALL_OFFICIAL_NAME = currentConfig.officialName;
const HALL_RESERVATION_ARRIVAL_MINUTES = currentConfig.arrivalMinutes;
const HALL_GOOGLE_ANALYTICS_KEY = currentConfig.googleAnalytics;
const HALL_GOOGLE_TAG_MANAGER_ID = currentConfig.googleTagManager;
const HALL_DEFAULT_BASENAME = currentConfig.basename;
const HALL_LANGUAGES = currentConfig.languages;
const SHOW_PER_PERSON_PRICE = currentConfig.showPerPersonPrice || false;
const SHOW_READ_TERMS_BUTTON = currentConfig.showReadTermsButton || false;
const ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = currentConfig.enableAdjacentHighlight || false;
const HALL_IS_ONLY_BOOKING = currentConfig.isOnlyBooking;
const HALL_HAS_BASENAME = currentConfig.hasBasename;

/* apply hall based variable values */
export const CURRENCY = HALL_CURRENCY;
export const CURRENCY_FRACTION_DIGITS = HALL_CURRENCY_FRACTION_DIGITS;
export const LOCALE = HALL_LOCALE;
export const TAX = HALL_TAX;
export const DEFAULT_LANGUAGE = HALL_DEFAULT_LANGUAGE;
export const DEFAULT_RESERVATION_DAYS_LIMIT = HALL_DEFAULT_RESERVATION_DAYS_LIMIT;
export const DEFAULT_HALL_ID = IS_DEV ? HALL_DEFAULT_HALL_ID : HALL_DEFAULT_HALL_ID;
export const DEFAULT_OFFICIAL_NAME = HALL_OFFICIAL_NAME;
export const DEFAULT_RESERVATION_ARRIVAL_MINUTES = HALL_RESERVATION_ARRIVAL_MINUTES;
export const GOOGLE_ANALYTICS_KEY = HALL_GOOGLE_ANALYTICS_KEY;
export const GOOGLE_TAG_MANAGER_ID = HALL_GOOGLE_TAG_MANAGER_ID;
export const DEFAULT_BASENAME = HALL_DEFAULT_BASENAME;
export const DEFAULT_RESERVATION_DAYS_FROM_NOW = HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW;
export const DEFAULT_SHOW_PER_PERSON_PRICE = SHOW_PER_PERSON_PRICE;
export const DEFAULT_SHOW_READ_TERMS_BUTTON = SHOW_READ_TERMS_BUTTON;
export const DEFAULT_ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT;
export const ANIMATIONS_ENABLED = IS_PROD;
export const IS_PAYMENT_TEST_MODE_ON = false;
export const PAYMENT_TEST = !IS_PROD && IS_PAYMENT_TEST_MODE_ON;
export const GENERIC_ARTICLE_ID = 3000;
export const IS_UPSELL_ALLOWED = true;
export const TIMER_COUNT = 10 * 1000 * 60;
export const PROJECT_VERSION = import.meta.env?.VITE_APP_VERSION || '0.1.0';
export const MAXIMUM_POINTS_COUNT = 10;
export const DEFAULT_COINS_COUNT = 1500;
export const IS_ONLY_BOOKING: boolean = HALL_IS_ONLY_BOOKING || false;
export const HAS_BASENAME: boolean | undefined = HALL_HAS_BASENAME || false;

export const HAS_HELP_ENABLED: boolean = false;
export const FILTER_TABS_ENABLED: boolean = true;
export const languages: Language[] = HALL_LANGUAGES || [];



export const CALENDAR_WEEK_RESERVATION_OVERVIEW = false;
export const CALENDAR_AGENDA_TIMESLOTS_CARD_MINI = true;
export const CALENDAR_WEEK_TIMESLOTS_CARD_MINI = true;
export const TIMESLOTS_FIRST_HOUR = 10;
export const TIMESLOTS_LAST_HOUR = 23;
export const MAXIMUM_DEFAULT_VALIDITY_WEEKS = 4;
export const OFFER_VALID_UNTIL_DAYS_BEFORE_EVENT = 7;

export interface Translations {
    en: string;
    de: string;
    nl: string;
    it?: string;
    hu?: string;
    sk?: string;
}
